/**
 * MedicalWebPage structured data.
 *
 * Emits one `MedicalWebPage` node per medical content page, linked back to the
 * sitewide MedicalClinic and physician Person nodes declared in
 * `src/routes/__root.tsx`. Keeping the registry in one place means the entity
 * IDs, reviewer, and specialty stay consistent across every page.
 */
import { schemaDateFields } from "@/lib/schema-dates";
import { ES_BY_EN } from "@/lib/es-pages";
import { EN_DRUG_PAGES, drugAboutAndMainEntity, drugNodes, drugRefs } from "@/lib/drug-schema";

const SITE = "https://virginiabariatric.com";
const ORG_ID = `${SITE}/#organization`;
export const PHYSICIAN_ID = `${SITE}/#physician-fitzer`;
const WEBSITE_ID = `${SITE}/#website`;

/** Hospital where Dr. Fitzer operates. Declared once in the sitewide graph. */
export const HOSPITAL_ID = `${SITE}/#inova-fair-oaks`;
export const HOSPITAL_REF = { "@id": HOSPITAL_ID } as const;
export const HOSPITAL_SCHEMA = {
  "@type": "Hospital",
  "@id": HOSPITAL_ID,
  name: "INOVA Fair Oaks Hospital",
  address: {
    "@type": "PostalAddress",
    streetAddress: "3600 Joseph Siewick Drive",
    addressLocality: "Fairfax",
    addressRegion: "VA",
    postalCode: "22033",
    addressCountry: "US",
  },
} as const;

/** Care-setting sentences used in each procedure's `howPerformed`. */
export const SETTING_OUTPATIENT =
  "Performed as an outpatient procedure at INOVA Fair Oaks Hospital; patients usually go home the same day.";
export const SETTING_ONE_NIGHT =
  "Performed at INOVA Fair Oaks Hospital, usually with a one-night hospital stay.";

/** Spanish care-setting sentences, the twins of SETTING_OUTPATIENT / SETTING_ONE_NIGHT. */
export const SETTING_OUTPATIENT_ES =
  "Se realiza como procedimiento ambulatorio en INOVA Fair Oaks Hospital; los pacientes normalmente se van a casa el mismo día.";
export const SETTING_ONE_NIGHT_ES =
  "Se realiza en INOVA Fair Oaks Hospital, normalmente con una noche de hospitalización.";

/** Registry pages whose MedicalWebPage is about the page's own #procedure node. */
const PROCEDURE_PAGES = new Set([
  "/services/sleeve",
  "/services/bypass",
  "/services/esg",
  "/services/balloon",
  "/services/band",
]);

/**
 * Authoritative external profiles for the single physician entity. Every node
 * that describes Dr. Fitzer carries this list so crawlers reconcile the
 * separate mentions into one "Matthew Fitzer, MD" entity.
 */
export const PHYSICIAN_SAME_AS = [
  "https://npiregistry.cms.hhs.gov/provider-view/1366489098",
  "https://www.doximity.com/pub/matthew-fitzer-md",
  "https://pubmed.ncbi.nlm.nih.gov/?term=Fitzer+MA&cauthor_id=22784128",
  "https://www.google.com/search?q=Dr.+Matthew+A.+Fitzer,+MD&stick=H4sIAAAAAAAA_-NgU1I1qLCwTDIzNk81sjA0S01MMbO0MqhItjAyM0qzSE0yNDM3NU01WsQq6VKkp-CbWFKSkVqu4Kin4JZZUpVapKPg6wIAyGCKkEUAAAA&hl=en&mat=CQOoob25R-yUElcBa0lj_8y0cekh27rqKmpm_pM5QMs6ZEuqfxE00wOyN6jftzHTub1XAAK8wnHzcWPpUpWAvHkLiXE2VLvfkLFzEuwZpcLADXiYe_95ZO5esXIVkrfLapI&authuser=0",
  "https://www.obesityhelp.com/profiles/bariatric-surgeon/dr-matthew-a-fitzer/overview",
  "https://www.healthgrades.com/physician/dr-matthew-fitzer-24smy",
  "https://www.bariatricpal.com/surgeons/898-matthew-a-fitzer/",
  "https://www.instagram.com/doctorfitzer/",
  "https://www.facebook.com/virginiabariatric/",
  "https://www.youtube.com/channel/UC4S6QghlleZYQ8fNLOSJgZw",
  `${SITE}/about`,
  `${SITE}/team`,
] as const;

/**
 * Bare pointer to the canonical physician node. Use inside a JSON-LD block
 * that already declares the full Person node (same @graph or same page).
 */
export const PHYSICIAN_ID_REF = { "@id": PHYSICIAN_ID } as const;


export type MedicalWebPageEntry = {
  /** Path portion of the canonical URL, e.g. "/services/sleeve". */
  path: string;
  name: string;
  description: string;
  /** Schema.org MedicalAudience / specialty hints. */
  about?: { type: string; name: string; alternateName?: string[] };
  /** Purpose of the page per schema.org WebPageType enumeration. */
  pageType?: string;
};

const PAGES: MedicalWebPageEntry[] = [
  {
    path: "/services",
    name: "Bariatric Surgery in Northern Virginia",
    description:
      "Bariatric surgery procedures offered by Dr. Matthew Fitzer in Northern Virginia — gastric sleeve surgery, gastric bypass, LAP-BAND, ESG, intragastric balloon, and revisional surgery.",
    about: { type: "MedicalProcedure", name: "Bariatric surgery" },
    pageType: "https://schema.org/MedicalWebPage",
  },
  {
    path: "/services/sleeve",
    name: "Gastric Sleeve Surgery (Sleeve Gastrectomy)",
    description:
      "How gastric sleeve surgery works, who qualifies, expected weight loss, risks, and recovery, with Virginia Bariatric Surgery's own patient outcome data.",
    about: {
      type: "MedicalProcedure",
      name: "Sleeve gastrectomy",
      alternateName: ["Gastric sleeve surgery", "VSG"],
    },
  },
  {
    path: "/services/bypass",
    name: "Roux-en-Y Gastric Bypass Surgery",
    description:
      "How Roux-en-Y gastric bypass works, who it suits, expected weight loss, effects on reflux and diabetes, risks, and recovery.",
    about: { type: "MedicalProcedure", name: "Roux-en-Y gastric bypass" },
  },
  {
    path: "/services/esg",
    name: "Endoscopic Sleeve Gastroplasty (ESG)",
    description:
      "An incisionless, same-day endoscopic weight-loss procedure that reshapes the stomach with internal sutures.",
    about: { type: "MedicalProcedure", name: "Endoscopic sleeve gastroplasty" },
  },
  {
    path: "/services/balloon",
    name: "Orbera® Intragastric Balloon",
    description:
      "A non-surgical, temporary gastric balloon program for patients seeking moderate weight loss without an operation.",
    about: { type: "MedicalProcedure", name: "Intragastric balloon placement" },
  },
  {
    path: "/services/band",
    name: "LAP-BAND® Adjustable Gastric Banding",
    description:
      "Adjustable gastric band placement, band adjustments, and band-related revision care in Northern Virginia.",
    about: { type: "MedicalProcedure", name: "Adjustable gastric banding" },
  },
  {
    path: "/medical-weight-loss/medications",
    name: "Weight-Loss Medications (GLP-1 & More) in Northern Virginia",
    description:
      "An introduction to FDA-approved weight-loss medications, including GLP-1 and GIP/GLP-1 therapies and oral options, prescribed and monitored by Dr. Fitzer.",
    about: { type: "MedicalTherapy", name: "Anti-obesity medication therapy", alternateName: ["GLP-1 receptor agonist therapy", "Weight-loss medication"] },
  },
  {
    path: "/medical-weight-loss/semaglutide",
    name: "Semaglutide (Wegovy and Ozempic) for Weight Loss",
    description:
      "Physician-supervised semaglutide (Wegovy, Ozempic) for weight loss in Herndon, VA, prescribed and monitored by a board-certified bariatric surgeon.",
    about: {
      type: "MedicalTherapy",
      name: "Semaglutide therapy",
      alternateName: ["Wegovy", "Ozempic", "GLP-1 receptor agonist"],
    },
  },
  {
    path: "/medical-weight-loss/tirzepatide",
    name: "Tirzepatide (Zepbound and Mounjaro) for Weight Loss",
    description:
      "Physician-supervised tirzepatide (Zepbound, Mounjaro) for weight loss in Northern Virginia, prescribed and monitored by Dr. Matthew Fitzer.",
    about: {
      type: "MedicalTherapy",
      name: "Tirzepatide therapy",
      alternateName: ["Zepbound", "Mounjaro", "GIP/GLP-1 receptor agonist"],
    },
  },
  {
    path: "/medical-weight-loss/oral-medications",
    name: "Oral Weight-Loss Medications (Phentermine, Contrave, Orlistat, Diethylpropion)",
    description:
      "Phentermine, Contrave®, orlistat and diethylpropion prescribed by a board-certified bariatric surgeon in Herndon, VA. Proven, affordable non-GLP-1 options.",
    about: { type: "MedicalTherapy", name: "Anti-obesity pharmacotherapy", alternateName: ["Phentermine", "Contrave", "Orlistat", "Diethylpropion"] },
  },

];


const REGISTRY = new Map(PAGES.map((entry) => [entry.path, entry]));

/**
 * Canonical postal address for the practice. Every Organization/LocalBusiness
 * node must carry this — validators require `address` on each node they parse,
 * and they parse each <script type="application/ld+json"> block in isolation.
 */
export const PRACTICE_ADDRESS = {
  "@type": "PostalAddress",
  streetAddress: "171 Elden Street, Suite 3B",
  addressLocality: "Herndon",
  addressRegion: "VA",
  postalCode: "20170",
  addressCountry: "US",
} as const;

/**
 * Self-contained organization reference. Use instead of a bare `{ "@id": ... }`
 * pointer so a standalone JSON-LD block still resolves a typed node with a
 * valid address.
 */
export const ORG_REF = {
  "@type": ["MedicalClinic", "Organization"],
  "@id": ORG_ID,
  name: "Virginia Bariatric Surgery",
  url: `${SITE}/`,
  telephone: "+1-703-709-9771",
  address: PRACTICE_ADDRESS,
} as const;

/**
 * Self-contained physician reference. `jobTitle` / `worksFor` are Person-only
 * properties and appear here only — never on an Organization node.
 */
export const PHYSICIAN_REF = {
  "@type": "Person",
  "@id": PHYSICIAN_ID,
  name: "Matthew Fitzer, MD",
  honorificSuffix: "MD",
  jobTitle: "Bariatric Surgeon",
  url: `${SITE}/about`,
  mainEntityOfPage: `${SITE}/about`,
  sameAs: PHYSICIAN_SAME_AS,
  worksFor: { "@id": ORG_ID },
} as const;

/**
 * Canonical business (NAP) node for the practice. Single source of truth so the
 * name, address, phone, hours, and profiles are byte-identical on every page
 * that declares the organization (root shell, home, About).
 *
 * MedicalClinic already inherits MedicalBusiness → LocalBusiness →
 * Organization, but plain `Organization` is declared explicitly as well so
 * validators that look for the literal type find it without walking the
 * schema.org hierarchy.
 */
export const ORGANIZATION_SCHEMA = {
  "@type": ["MedicalClinic", "Organization"],
  "@id": ORG_ID,
  name: "Virginia Bariatric Surgery",
  alternateName: ["Virginia Bariatric", "Virginia Bariatric Center", "Virginia Bariatric Surgery Center"],
  legalName: "Virginia Bariatric Surgery, P.C.",
  description:
    "Bariatric surgery and physician-supervised medical weight loss in Herndon, Virginia, serving the Northern Virginia and Washington, D.C. metro area.",
  url: `${SITE}/`,
  logo: `${SITE}/logo.png`,
  image:
    `${SITE}/__l5e/assets-v1/64a2217d-5454-444b-b82e-298e33a87aa9/og-hero.jpg`,

  telephone: "+1-703-709-9771",
  faxNumber: "+1-888-519-0045",
  email: "Val@virginiabariatric.com",
  priceRange: "$$",
  currenciesAccepted: "USD",
  paymentAccepted: "Cash, Credit Card, Insurance",
  medicalSpecialty: ["https://schema.org/Surgical", "https://schema.org/DietNutrition"],
  isAcceptingNewPatients: true,
  address: PRACTICE_ADDRESS,
  geo: { "@type": "GeoCoordinates", latitude: 38.9695, longitude: -77.3861 },
  hasMap: "https://www.google.com/maps/search/?api=1&query=171+Elden+Street+Suite+3B+Herndon+VA+20170",
  openingHoursSpecification: [
    {
      "@type": "OpeningHoursSpecification",
      dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
      opens: "08:00",
      closes: "16:30",
    },
  ],
  areaServed: [
    { "@type": "City", name: "Herndon" },
    { "@type": "City", name: "Reston" },
    { "@type": "City", name: "Fairfax" },
    { "@type": "City", name: "Arlington" },
    { "@type": "City", name: "Alexandria" },
    { "@type": "City", name: "Sterling" },
    { "@type": "City", name: "Ashburn" },
    { "@type": "City", name: "Chantilly" },
    { "@type": "City", name: "Manassas" },
    { "@type": "AdministrativeArea", name: "Loudoun County" },
    { "@type": "AdministrativeArea", name: "Northern Virginia" },
    { "@type": "AdministrativeArea", name: "Washington, D.C. metro area" },
  ],
  contactPoint: [
    {
      "@type": "ContactPoint",
      telephone: "+1-703-709-9771",
      contactType: "customer service",
      areaServed: "US",
      availableLanguage: ["English"],
    },
  ],
  availableService: [
    {
      "@type": "MedicalTherapy",
      "@id": `${SITE}/medical-weight-loss/medications#service`,
      name: "Weight-Loss Medication",
      description:
        "GLP-1 medications and physician-supervised medical weight-loss programs, with telehealth visits available.",
      url: `${SITE}/medical-weight-loss/medications`,
    },
    {
      "@type": "MedicalProcedure",
      "@id": `${SITE}/services/sleeve#service`,
      name: "Gastric Sleeve Surgery (Sleeve Gastrectomy)",
      description:
        "Northern Virginia's most-performed weight-loss operation, with approximately 70% excess weight loss.",
      url: `${SITE}/services/sleeve`,
      bodyLocation: "stomach",
    },
    {
      "@type": "MedicalProcedure",
      "@id": `${SITE}/services/bypass#service`,
      name: "Gastric Bypass (Roux-en-Y)",
      description:
        "Roux-en-Y gastric bypass for significant, long-term weight loss and metabolic improvement.",
      url: `${SITE}/services/bypass`,
      bodyLocation: "stomach",
    },
    {
      "@type": "MedicalProcedure",
      "@id": `${SITE}/services/band#service`,
      name: "Adjustable Gastric Banding",
      description:
        "Adjustable, reversible band for gradual weight loss without removing stomach tissue.",
      url: `${SITE}/services/band`,
      bodyLocation: "stomach",
    },
    {
      "@type": "MedicalProcedure",
      "@id": `${SITE}/services/esg#service`,
      name: "Endoscopic Sleeve Gastroplasty (ESG)",
      description: "Non-surgical endoscopic procedure that reshapes the stomach from inside.",
      url: `${SITE}/services/esg`,
      bodyLocation: "stomach",
      procedureType: "https://schema.org/NoninvasiveProcedure",
    },
    {
      "@type": "MedicalProcedure",
      "@id": `${SITE}/services/balloon#service`,
      name: "Intragastric Balloon",
      description: "Non-surgical Orbera balloon for temporary weight reduction and habit reset.",
      url: `${SITE}/services/balloon`,
      bodyLocation: "stomach",
      procedureType: "https://schema.org/NoninvasiveProcedure",
    },
    {
      "@type": "MedicalProcedure",
      "@id": `${SITE}/services/revisions#service`,
      name: "Revisional Bariatric Surgery",
      description: "Expert correction or conversion of prior bariatric procedures.",
      url: `${SITE}/services/revisions`,
      bodyLocation: "stomach",
    },
  ],
  /**
   * Authoritative external profiles. Engines reconcile these mentions into one
   * business entity, so only verified, owned profiles belong here.
   */
  sameAs: [
    "https://www.google.com/search?q=Virginia+Bariatric+Surgery&stick=H4sIAAAAAAAA_-NgU1I1qLCwTDIzsTBMMUsxSjE2Sk2xMqgwTTQxN0kzNTQ1SjU0MzZOXsQqFZZZlJ6Zl5mo4JRYlJlYUpSZrBBcWpSeWlQJADtx_whGAAAA&hl=en&mat=CeLmZpRNEODcElcBa0lj_4uI3G31pDQcTWV-RbwaVpChbXe01GBC1lxv6EiCt-Ugz_flOjC3uaRM8TLriHRwuA1aR34XLRZKwhwljmb2Hg9eBUG2-vdn_KP-k974vjnK9rs&authuser=0",
    "https://www.facebook.com/virginiabariatric/",
    "https://www.instagram.com/doctorfitzer/",
    "https://www.youtube.com/@virginiabariatricsurgeryce114",
    "https://www.youtube.com/channel/UC4S6QghlleZYQ8fNLOSJgZw",
    "https://www.healthgrades.com/physician/dr-matthew-fitzer-24smy",
    "https://www.google.com/maps/search/?api=1&query=171+Elden+Street+Suite+3B+Herndon+VA+20170",
  ],

  founder: { "@id": PHYSICIAN_ID },
  employee: { "@id": PHYSICIAN_ID },
} as const;


/**
 * Build the MedicalWebPage JSON-LD node for a registered path.
 * Returns `null` when the path is not a registered medical page.
 */
export function buildMedicalWebPage(path: string) {
  const entry = REGISTRY.get(path);
  if (!entry) return null;

  const url = `${SITE}${entry.path}`;

  // Reciprocal of the Spanish page's `translationOfWork`, from the same map
  // that drives hreflang, so schema and link annotations can never disagree.
  const spanishPath = ES_BY_EN[entry.path];

  return {
    "@context": "https://schema.org",
    "@type": "MedicalWebPage",
    // Use the canonical `#webpage` id so FAQPage/ImageObject nodes that
    // reference `${url}#webpage` resolve instead of dangling.
    "@id": `${url}#webpage`,
    breadcrumb: { "@id": `${url}#breadcrumb` },
    url,
    name: entry.name,
    description: entry.description,
    inLanguage: "en-US",
    isPartOf: { "@id": WEBSITE_ID },
    ...(PROCEDURE_PAGES.has(entry.path)
      ? {
          about: { "@id": `${url}#procedure` },
          mainEntity: { "@id": `${url}#procedure` },
          mentions: [HOSPITAL_REF],
        }
      : (() => {
          const therapy = entry.about
            ? { "@type": entry.about.type, name: entry.about.name, ...(entry.about.alternateName ? { alternateName: entry.about.alternateName } : {}) }
            : undefined;
          const drugPage = EN_DRUG_PAGES.find((d) => d.path === entry.path);
          if (drugPage) return drugAboutAndMainEntity(drugPage, therapy ? [therapy] : []);
          // The medications introduction references the spoke pages' Drug
          // nodes without duplicating them.
          if (entry.path === "/medical-weight-loss/medications") {
            return { about: therapy, mentions: EN_DRUG_PAGES.flatMap(drugRefs) };
          }
          return { about: therapy };
        })()),
    medicalAudience: { "@type": "MedicalAudience", audienceType: "Patient" },
    specialty: "https://schema.org/Surgical",

    ...schemaDateFields(entry.path, "2026-08-08"),
    reviewedBy: PHYSICIAN_REF,
    author: PHYSICIAN_REF,
    publisher: ORG_REF,
    provider: ORG_REF,
    mainEntityOfPage: url,
    ...(spanishPath ? { workTranslation: { "@id": `${SITE}${spanishPath}#webpage` } } : {}),
  };
}

/**
 * `workTranslation` link for an English page that has a Spanish twin, spread
 * into that page's WebPage node. Pairs with `translationOfWork` on the Spanish
 * side so structured data states the same relationship as hreflang.
 * Returns `{}` when the page has no Spanish counterpart.
 */
export function workTranslationOf(enPath: string) {
  const spanishPath = ES_BY_EN[enPath];
  return spanishPath ? { workTranslation: { "@id": `${SITE}${spanishPath}#webpage` } } : {};
}

/**
 * `translationOfWork` link back to the English original, for Spanish pages
 * that build their WebPage node inline instead of through `esWebPageSchema`.
 */
export function translationOfWork(enUrl: string) {
  return { translationOfWork: { "@id": `${enUrl.replace(/\/$/, "")}#webpage` } };
}

/**
 * Head `scripts` entry for a registered medical page, or an empty array.
 * Spread it into a route's `scripts` array.
 */
export function medicalWebPageScript(path: string) {
  const node = buildMedicalWebPage(path);
  if (!node) return [];
  const drugPage = EN_DRUG_PAGES.find((d) => d.path === path);
  if (drugPage) {
    // Keep the Drug nodes in the same block so about/mainEntity resolve
    // even for validators that read each JSON-LD block in isolation.
    const { "@context": context, ...page } = node;
    return [
      {
        type: "application/ld+json",
        children: JSON.stringify({ "@context": context, "@graph": [page, ...drugNodes(drugPage)] }),
      },
    ];
  }
  return [{ type: "application/ld+json", children: JSON.stringify(node) }];
}

/**
 * Rights metadata for images we own, so validators never flag them as missing
 * licensing info. `pageUrl` is the page the image is licensed from.
 */
export function imageRights(pageUrl: string) {
  return {
    creditText: "Virginia Bariatric Surgery",
    copyrightNotice: "\u00a9 Virginia Bariatric Surgery",
    copyrightHolder: { "@type": "Organization", name: "Virginia Bariatric Surgery" },
    creator: { "@type": "Organization", name: "Virginia Bariatric Surgery" },
    license: pageUrl,
    acquireLicensePage: `${SITE}/contact`,
    usageInfo: `${SITE}/contact`,
  } as const;
}

/**
 * Canonical practice attribution for every MedicalProcedure node.
 * `performer`/`provider` are not defined on MedicalProcedure in Schema.org
 * (validators flag them), so we attribute the procedure with
 * `recognizingAuthority`, which is valid on MedicalEntity.
 */
export const PROCEDURE_ACTORS = {
  recognizingAuthority: { "@id": ORG_ID },
} as const;
